/**
 * The cache reconcile (gh #4775 T4, Q1-A): `POST /api/users/me/cache/reconcile`.
 *
 * A device that keeps conversation pages, chat-hub history threads and person-to-person pages on
 * disk names them here, and the server answers which of them it must drop and which it could not
 * decide. Whatever the body names and the answer lists in neither is the caller's to keep. The
 * device asks at launch and at every cold resync, because neither replays the frames that said
 * a conversation was erased or a hub was lost, and keeps every entry unpainted until it is
 * answered.
 *
 * - `drop`: the caller may not read it any more, whatever the reason: deleted, erased, never
 *   created, another person's, a hub or a support role lost, a people thread gone, turned
 *   read-only or not a personal 1:1. One answer for all of them, so the route tells a caller
 *   nothing about what they cannot read.
 * - `unknown`: a read the answer needed failed, or the hub names the caller while their hub list
 *   does not. The device keeps it unpainted and asks again at its next reconcile. Never `keep`.
 *
 * Interactive sign-in sessions only: a scoped API token is refused.
 */

import { z } from 'zod';
import { uuidSchema } from './common';
import { navItemSchema } from './live-updates';
import type { PeopleThreadSummary } from './people-threads';

/** At most this many ids in one body: conversation pages, history transcripts and people threads together. */
export const CACHE_RECONCILE_MAX_IDS = 300;

/**
 * At most this many distinct hubs in one body: the route reads each hub once. A device persists
 * at most 10 conversation pages and 5 history threads (T4's disk bounds), so 15.
 */
export const CACHE_RECONCILE_MAX_HUBS = 15;

/**
 * Who a cached page was read as — the nav it was read under (`navItemSchema`): `chat` and `task`
 * are the owner's own pages (no internal rows), `support` a team member's or admin's (internal
 * rows included). Never served across scopes.
 */
export const cacheReconcileScopeSchema = navItemSchema;
export type CacheReconcileScope = z.infer<typeof cacheReconcileScopeSchema>;

/** A conversation's first page (`messageStore`), under the scope it was read with. */
export const cachedConversationPageSchema = z
  .object({
    conversation_id: uuidSchema,
    hub_id: uuidSchema,
    scope: cacheReconcileScopeSchema,
  })
  .strict();
export type CachedConversationPage = z.infer<typeof cachedConversationPageSchema>;

/**
 * A chat hub's history thread (`conversationHistory`): its hub, whose thread it is
 * (`hub_user_id`: the caller's own user id on `chat`/`task`, the contact's on `support`), and
 * the ended conversations whose transcripts it holds.
 */
export const cachedHistoryThreadSchema = z
  .object({
    hub_id: uuidSchema,
    hub_user_id: z.string().min(1).max(128),
    nav: cacheReconcileScopeSchema,
    conversation_ids: z.array(uuidSchema).min(1).max(CACHE_RECONCILE_MAX_IDS),
  })
  .strict();
export type CachedHistoryThread = z.infer<typeof cachedHistoryThreadSchema>;

/**
 * Whether a person-to-person page may be kept on disk, from the viewer's own thread-list row: a
 * personal 1:1 they may write to — never a group or an org-linked thread (T4's disk rule; a block,
 * a removed contact or the counterpart's deleted account turns a 1:1 read-only). The server's keep
 * rule for `threads`; a device persisting people pages applies the same one.
 */
export function isPersistablePeopleThread(row: Pick<PeopleThreadSummary, 'kind' | 'org_id' | 'writable'>): boolean {
  return row.kind === 'dm' && row.org_id === null && row.writable;
}

/** A person-to-person thread's page (`peopleStore`). */
export const cachedPeopleThreadSchema = z.object({ thread_id: uuidSchema }).strict();
export type CachedPeopleThread = z.infer<typeof cachedPeopleThreadSchema>;

/** How many ids `body` names: each page, each history transcript and each people thread. */
export function cacheReconcileIdCount(body: {
  conversations: readonly unknown[];
  histories: ReadonlyArray<{ conversation_ids: readonly unknown[] }>;
  threads: readonly unknown[];
}): number {
  return body.conversations.length
    + body.histories.reduce((n, history) => n + history.conversation_ids.length, 0)
    + body.threads.length;
}

/** POST /api/users/me/cache/reconcile — every entry the device holds on disk. */
export const cacheReconcileBody = z
  .object({
    conversations: z.array(cachedConversationPageSchema).max(CACHE_RECONCILE_MAX_IDS).default([]),
    histories: z.array(cachedHistoryThreadSchema).max(CACHE_RECONCILE_MAX_IDS).default([]),
    threads: z.array(cachedPeopleThreadSchema).max(CACHE_RECONCILE_MAX_IDS).default([]),
  })
  .strict()
  .superRefine((body, ctx) => {
    if (cacheReconcileIdCount(body) > CACHE_RECONCILE_MAX_IDS) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `At most ${CACHE_RECONCILE_MAX_IDS} ids in all` });
    }
    const hubs = new Set([...body.conversations, ...body.histories].map((entry) => entry.hub_id));
    if (hubs.size > CACHE_RECONCILE_MAX_HUBS) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `At most ${CACHE_RECONCILE_MAX_HUBS} distinct hubs` });
    }
  });
export type CacheReconcileBody = z.infer<typeof cacheReconcileBody>;

/**
 * One verdict's entries, in the body's own shape. A history thread lists only the transcripts the
 * verdict names; a thread whose hub or nav the caller lost lists all of them.
 */
export interface CacheReconcileVerdicts {
  conversations: CachedConversationPage[];
  histories: CachedHistoryThread[];
  threads: CachedPeopleThread[];
}

export interface CacheReconcileResult {
  /** The caller may not read these any more: delete them from disk. */
  drop: CacheReconcileVerdicts;
  /** Not decided this time: keep them unpainted and ask again. */
  unknown: CacheReconcileVerdicts;
}

/** POST /api/users/me/cache/reconcile */
export interface CacheReconcileResponse {
  success: true;
  data: CacheReconcileResult;
}
